import { useMemo, useState } from "react";

import { compareDistrictNames } from "../../utils/postcodeDistricts";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { WizardState, formatGbp } from "./types";

interface PricingStepProps {
  state: WizardState;
  onPriceChange: (district: string, price: number) => void;
  onApplyDefault: (price: number) => void;
}

export default function PricingStep({ state, onPriceChange, onApplyDefault }: PricingStepProps) {
  const [bulkPrice, setBulkPrice] = useState(state.defaultPrice);

  const selected = useMemo(
    () => state.districts.filter((d) => d.selected).sort((a, b) => compareDistrictNames(a.district, b.district)),
    [state.districts]
  );

  const byArea = useMemo(() => {
    const groups = new Map<string, typeof selected>();
    selected.forEach((d) => groups.set(d.area, [...(groups.get(d.area) ?? []), d]));
    return Array.from(groups.entries());
  }, [selected]);

  if (selected.length === 0) {
    return <p className="text-sm text-gray-600">You haven't selected any districts. Go back and pick at least one.</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Set your collection prices</h2>
        <p className="mt-1 text-sm text-gray-600">
          Every district starts at your default of {formatGbp(state.defaultPrice)}. Adjust any district that costs
          you more or less to collect from — you can always change these later under Postcode Prices.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">Set every district to (£)</label>
          <input
            type="number"
            min={0}
            step={1}
            value={bulkPrice}
            onChange={(e) => setBulkPrice(Math.max(0, +e.target.value || 0))}
            className="mt-1 w-32 rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <ButtonInput isSubmit={false} label="Apply to all" onClick={() => onApplyDefault(bulkPrice)} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {byArea.map(([area, districts]) => (
          <div key={area} className="rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
              {area} area <span className="font-normal text-gray-500">· {districts.length} districts</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {districts.map((d) => (
                <li key={d.district} className="flex items-center gap-3 px-3 py-2">
                  <span className="w-14 text-sm font-semibold text-gray-900">{d.district}</span>
                  <span className="flex-1 truncate text-xs text-gray-500">{d.town ?? ""}</span>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 text-xs text-gray-500">£</span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={d.price}
                      onChange={(e) => onPriceChange(d.district, Math.max(0, +e.target.value || 0))}
                      className="w-24 rounded-md border-gray-300 pl-5 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      aria-label={`Price for ${d.district}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
